import React, {
    useContext, useEffect, useMemo, useRef,
} from 'react';
import { tp } from 'app/utils/typograf';
import { useQuery, useReactiveVar } from '@apollo/client';
import { DishesSelectionContext } from 'app/containers/contexts/dishesSelection.context';
import classNames from 'classnames';
import { analyticService } from 'global/services';
import { maxiBlockContentKeyVar, selectedPeriodVar } from 'app/apollo/reaction';
import { isBannerPeriodActive } from 'app/utils/bannerPeriod';
import TOP_MENU_DISH from 'app/graphql/network/menuDish';
import { useFirstDishAvailablePeriod } from 'app/views/Basket/components/BasketNewUpsell/useFirstDishAvailablePeriod';
import { RENDERING_CONTEXT_MENU, useDishDetails } from '../MobileDishDetails';
import css from './PromoMaxiBlock.module.scss';

interface IPromoMaxiBlockProps {
    contentKey: 'kids' | 'sauces',
    selectionKey: string,
    observer: any,
    trackName?: string,
}

const promoBlocksData: any = {
    kids: {
        title: 'Детское',
        subtitle: 'меню',
        description: {
            firstLine: 'Хиты всех детей с 4 лет (и до 99).',
            secondLine: 'Весело готовить вместе!',
        },
        backgroundImageClass: 'kids',
        trackName: 'Collections_kids',
        // periods: ['2026-09-07', '2026-09-14'],
    },
    sauces: {
        title: 'Соусы',
        subtitle: 'от шеф-повара',
        description: {
            firstLine: 'Наши авторские хиты: песто, луковый джем, блючиз и другие',
            secondLine: '',
        },
        backgroundImageClass: 'sauces',
        trackName: 'Collections_sauses',
    },
    coast: {
        title: 'Лазурный',
        subtitle: 'берег',
        description: {
            firstLine: 'шум волн, тёплый ветер, розовый',
            secondLine: 'закат и изысканный ужин',
        },
        backgroundImageClass: 'coast',
        trackName: 'Collections_coast',
    },
    gryadka: {
        title: 'С грядки',
        subtitle: 'из деревни',
        description: {
            firstLine: 'Салат из свежих овощей,',
            secondLine: 'корзина спелых ягод и окрошка',
        },
        backgroundImageClass: 'gryadka',
        trackName: 'Collections_gryadka',
        periods: ['2026-08-31'],
    },
    ecomilk: {
        id: '5505',
        title: 'Готовим шедевры',
        subtitle: 'с творожным сыром',
        href: '/ecomilk?erid=2SDnjdQuXk7',
        description: {
            firstLine: '',
            secondLine: '',
        },
        backgroundImageClass: 'ecomilk',
        trackName: 'Collections_ecomilk',
        // isOpenDetails: true,
        periods: ['2026-08-31', '2026-09-07', '2026-09-14', '2026-09-21', '2026-09-28', '2026-10-05'],
    },
    school: {
        id: '5505',
        title: '',
        subtitle: '',
        description: {
            firstLine: '',
            secondLine: '',
        },
        backgroundImageClass: 'school',
        trackName: 'Collections_school',
        isOpenDetails: true,
        periods: ['2026-08-31', '2026-09-07', '2026-09-14', '2026-09-21', '2026-09-28'],
        buttonText: 'Подарить учителю',
    },
    halloween: {
        id: '4695',
        title: 'Ужасно вкусный\nбокс на Хэллоуин',
        subtitle: '',
        description: {
            firstLine: '',
            secondLine: '',
        },
        backgroundImageClass: 'halloween',
        trackName: 'Collections_halloween',
        isOpenDetails: true,
        periods: ['2026-10-12', '2026-10-19'],
    },
    harry: {
        title: 'Еда и сладости\nиз Хогвартса',
        subtitle: '',
        description: {
            firstLine: '',
            secondLine: '',
        },
        backgroundImageClass: 'harry',
        trackName: 'Collections_harry',
        periods: [
            '2026-10-26',
            '2026-11-02',
            '2026-11-09',
            '2026-11-16',
            '2026-11-23',
        ],
        buttonText: 'Алохомора!',
    },
};

// активен ли промо-блок в выбранный период (без periods - всегда активен)
export const isPromoBlockActive = (contentKey: string, selectedPeriod: string) => {
    const { periods = null } = promoBlocksData[contentKey] as any;
    return !periods || isBannerPeriodActive(periods, selectedPeriod);
};

export const PromoMaxiBlock = ({ contentKey, selectionKey, observer }: IPromoMaxiBlockProps) => {
    const {
        title, subtitle, description, backgroundImageClass, trackName,
        isOpenDetails = false, id = null, buttonText = '', href = null,
    } = promoBlocksData[contentKey] as any;
    const selectedPeriod = useReactiveVar(selectedPeriodVar);
    const { onOpenSelection } = useContext<any>(DishesSelectionContext);
    const rootRef = useRef<any>(null);
    const { openDishDetails } = useDishDetails(() => { });

    /* Блюдо может отсутствовать в выбранном цикле — ищем первый период,
     * начиная с текущего, где оно есть в меню */
    const { firstAvailablePeriod } = useFirstDishAvailablePeriod(id);

    /* Период для добавления: заполнен только когда блюдо нашлось в чужом цикле */
    const targetPeriod = useMemo(
        () => (id && firstAvailablePeriod && firstAvailablePeriod !== selectedPeriod
            ? firstAvailablePeriod
            : null),
        [id, firstAvailablePeriod, selectedPeriod],
    );

    /* Подгружаем блюдо найденного периода в кэш, чтобы деталка была заполнена */
    useQuery(TOP_MENU_DISH, {
        variables: { id, period: firstAvailablePeriod },
        skip: !id || !firstAvailablePeriod,
    });

    const handleSendAnalytics = () => {
        analyticService.push({
            eventName: 'Collection_Click',
            banner_name: contentKey,
            source_name: 'menu',
        });
    };

    const handleOpenSelection = () => {
        handleSendAnalytics();
        onOpenSelection({ tag: `maxi_${selectionKey}`, targetRealm: 'new_upsell' });
    };

    const handleOpenDetails = () => {
        if (!id) return;
        /* Если блюда нет в текущем цикле, деталка добавляет его в найденный
         * период — так же, как кнопка топ-блюда в корзине */
        openDishDetails(
            id,
            RENDERING_CONTEXT_MENU,
            targetPeriod ? { period: targetPeriod } : undefined,
        );
    };

    const rootStyles = classNames({
        maxiBlock: true,
        [css.maxiBlockRoot]: true,
        [css.mobileOnly]: true,
        [css.v2]: true,
    });
    const rootContentStyles = classNames({
        [css.maxiBlockContent]: true,
        [css[backgroundImageClass]]: true,
        [css.smallRadius]: false,
        [css.menu]: true,
    });
    const buttonStyles = classNames({
        [css.maxiBlockButton]: true,
        [css[contentKey]]: contentKey !== undefined,
        [css.uiButtonPrimary]: true,
        [css.colored]: true,
    });

    useEffect(
        () => {
            maxiBlockContentKeyVar(contentKey);
        },
        [contentKey],
    );
    useEffect(
        () => {
            if (rootRef.current) {
                observer.observe(rootRef.current);
            }
        },
        [rootRef, observer],
    );

    if (!isPromoBlockActive(contentKey, selectedPeriod)) {
        return null;
    }

    return (
        <div
            className={rootStyles}
            ref={rootRef}
            onClick={() => {
                if (href) {
                    window.location.href = href;
                    return;
                }
                if (isOpenDetails) {
                    handleOpenDetails();
                } else {
                    handleOpenSelection();
                }
            }}
            role="none"
            data-selection_key={contentKey}
            data-scroll_name={trackName}
        >
            <div className={rootContentStyles}>
                <div className={css.maxiBlockTitle}>
                    {title.split('\n').map((line: string, index: number) => (
                        <React.Fragment key={line}>
                            {index > 0 && <br />}
                            {line}
                        </React.Fragment>
                    ))}
                    <br />
                    {tp.execute(subtitle)}
                </div>
                <div className={css.maxiBlockDescription}>
                    {description.firstLine}
                    <br />
                    {description.secondLine}
                </div>
                <div
                    className={css.maxiBlockButtonLink}
                >
                    <div className={buttonStyles}>
                        <div className={css.uiButtonPrimaryText}>{buttonText || 'Хочу попробовать'}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
